// Global Instructions Rule Applied!
//
// Agent Access — the "Edit agent access" modal.
//
// This was the Connect modal. It is not any more: the in-app "Connect agent"
// button is gone, because it never connected anything. The transport
// connection is always agent-initiated — the user pastes the connector URL
// into their assistant, signs in with Microsoft, and the gateway's own
// consent page collects consent mid-handshake. All the button ever did was
// pre-issue the grant record so that consent page would be skipped, while
// telling the user nothing about how to actually connect.
//
// So Connected Agents is now purely the LIFECYCLE surface, and this modal is
// its edit half: change which tools an existing grant consents to, whether
// each write tool asks first or acts immediately, and when access expires.
//
// Everything offered here is still cfg-driven via globalConfigs.agent_access
// (design §8): the per-tool checkboxes (cfg.agent_tools), the expiry
// presets/cap (cfg.app_config group 'agent') and the §5 data-disclosure copy.
// The agent itself is fixed by the grant — there is no picker, and the RPC
// takes no agent_id, so an edit can never repoint a grant at another product.
//
// Each consented WRITE tool picks a mode, unchanged from consent:
//   * "Ask me first" — the agent files a proposal and nothing exists until
//     the user approves it under Agent activity.
//   * "Create without asking" — the agent creates the record immediately.
//     Offered only where cfg allows it (allowed_modes ∋ 'live'), and the
//     resulting live_tools list is re-validated server-side and becomes the
//     signed table allowlist the DB write funnels enforce.
//
// Editing may widen as well as narrow: this is a full re-consent by the row's
// owner, which is exactly what the Connect modal already permitted, and it is
// the likely reason someone opens it ("I now also want it to create deals").
// cfg's allowed_modes and the update_agent_grant RPC still bound it.

import React, { useEffect, useMemo, useState } from 'react'
import { Alert, Checkbox, Modal, Radio, Space, Typography } from 'antd'
import DatePicker from '../../../core/components/DatePicker.tsx'
import dayjs, { Dayjs } from 'dayjs'
import type {
  AgentAccessConfig,
  AgentAccessTool,
  AgentGrant
} from '../../../state_manager/data_models/agent_access_model.ts'
import type { AgentGrantScopePayload } from '../services/agentAccessService.ts'

const { Text } = Typography

const CUSTOM_EXPIRY = 'custom'

interface EditGrantModalProps {
  open: boolean
  saving: boolean
  agentAccess: AgentAccessConfig
  // The grant being edited. Null while closed — the modal renders nothing
  // rather than half-seeded state.
  grant: AgentGrant | null
  onCancel: () => void
  onSave: (grantId: string, payload: AgentGrantScopePayload) => Promise<boolean>
}

export function EditGrantModal({
  open,
  saving,
  agentAccess,
  grant,
  onCancel,
  onSave
}: EditGrantModalProps) {
  // Read tools first, then the write tools (which carry the mode selector).
  const consentTools: AgentAccessTool[] = useMemo(() => {
    const tools = (agentAccess.tools || []).filter((t) => t.kind === 'read' || t.kind === 'write')
    return [...tools.filter((t) => t.kind === 'read'), ...tools.filter((t) => t.kind === 'write')]
  }, [agentAccess])
  const consent = agentAccess.consent

  const [selectedTools, setSelectedTools] = useState<string[]>([])
  const [liveTools, setLiveTools] = useState<string[]>([])
  const [expiryChoice, setExpiryChoice] = useState<number | typeof CUSTOM_EXPIRY>(30)
  const [customExpiry, setCustomExpiry] = useState<Dayjs | null>(null)
  // Tools the grant consents to that cfg no longer offers (retired or
  // disabled since it was issued — the propose_deal → create_deal rename is
  // the precedent). Dropped from the payload rather than resubmitted, since
  // the RPC would reject the whole edit on "Unknown or disabled tool".
  const [droppedTools, setDroppedTools] = useState<string[]>([])

  const defaultExpiryChoice = useMemo(
    () =>
      (consent.expiry_days_options || []).includes(consent.expiry_days_default)
        ? consent.expiry_days_default
        : (consent.expiry_days_options || [])[0] ?? CUSTOM_EXPIRY,
    [consent]
  )

  // Seed from the grant on every open. Unlike the old Connect modal — which
  // re-baselined to reads-ticked / writes-unticked — an edit must start from
  // what the user actually consented to, or "Save changes" would silently
  // revoke capabilities they never touched.
  useEffect(() => {
    if (!open || !grant) return
    const known = new Set(consentTools.map((t) => t.tool_key))
    const granted = grant.agent_grants__scopes.tools || []
    const grantedLive = grant.agent_grants__scopes.live_tools || []

    setSelectedTools(granted.filter((t) => known.has(t)))
    setDroppedTools(granted.filter((t) => !known.has(t)))
    setLiveTools(grantedLive.filter((t) => known.has(t)))

    // Preselect the grant's current expiry as a custom date — but only when
    // it is still in the future. disabledDate below rejects anything before
    // today, so an already-expired grant cannot show its own date; it falls
    // back to the cfg default, which is also the sane default for renewing.
    const current = dayjs(grant.agent_grants__expires_at)
    if (current.isValid() && current.isAfter(dayjs())) {
      setExpiryChoice(CUSTOM_EXPIRY)
      setCustomExpiry(current)
    } else {
      setExpiryChoice(defaultExpiryChoice)
      setCustomExpiry(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, grant])

  const expiresAt: string | null = useMemo(() => {
    if (expiryChoice === CUSTOM_EXPIRY) {
      return customExpiry ? customExpiry.endOf('day').toISOString() : null
    }
    return dayjs().add(expiryChoice, 'day').toISOString()
  }, [expiryChoice, customExpiry])

  // Unticking a tool must also drop its live flag from everything the user
  // sees and from what we send. liveTools is kept raw so that re-ticking a
  // tool restores the mode the user last chose, but nothing downstream may
  // read it directly — otherwise the "acts without asking" warning stays up
  // after the last live tool has been unticked, which is exactly backwards.
  const effectiveLiveTools = useMemo(
    () => liveTools.filter((t) => selectedTools.includes(t)),
    [liveTools, selectedTools]
  )

  const isExpired = useMemo(
    () => (grant ? dayjs(grant.agent_grants__expires_at).isBefore(dayjs()) : false),
    [grant]
  )

  const canSave = Boolean(grant) && selectedTools.length > 0 && Boolean(expiresAt)

  const handleOk = async () => {
    if (!grant || !expiresAt) return
    const ok = await onSave(grant.agent_grants__id, {
      tools: selectedTools,
      live_tools: effectiveLiveTools,
      expires_at: expiresAt
    })
    if (ok) onCancel()
  }

  const setToolMode = (toolKey: string, live: boolean) =>
    setLiveTools((current) =>
      live ? [...new Set([...current, toolKey])] : current.filter((t) => t !== toolKey)
    )

  return (
    <Modal
      title='Edit agent access'
      open={open}
      onCancel={onCancel}
      onOk={handleOk}
      okText={isExpired ? 'Renew access' : 'Save changes'}
      okButtonProps={{ disabled: !canSave, loading: saving }}
      width={560}
    >
      <Space direction='vertical' size='middle' style={{ width: '100%' }}>
        {consent.disclosure ? (
          <Alert showIcon type='warning' message='Data disclosure' description={consent.disclosure} />
        ) : null}

        <div>
          <Text strong>Agent</Text>
          {/* Read-only: the grant fixes the agent. Same visual weight as the
              picker it replaces so the form does not lose its first row. */}
          <div style={{ marginTop: 4 }}>
            <Text>{grant?.agent_grants__agent_name}</Text>
            <Text type='secondary' style={{ marginLeft: 8, fontSize: 12 }}>
              {grant?.agent_grants__agent_id}
            </Text>
          </div>
        </div>

        {isExpired ? (
          <Alert
            showIcon
            type='info'
            message='This access has expired'
            description='The agent cannot act until you set a new expiry date below. Saving with a future date restores it — its history and activity are kept.'
          />
        ) : null}

        {droppedTools.length > 0 ? (
          <Alert
            showIcon
            type='info'
            message='Some capabilities are no longer offered'
            description={`This connection still lists ${droppedTools.join(', ')}, which ${
              droppedTools.length === 1 ? 'is' : 'are'
            } no longer available. Saving removes ${
              droppedTools.length === 1 ? 'it' : 'them'
            }; nothing else changes.`}
          />
        ) : null}

        <div>
          <Text strong>What it may do on your behalf</Text>
          <Checkbox.Group
            value={selectedTools}
            onChange={(values) => setSelectedTools(values as string[])}
            style={{ width: '100%', marginTop: 4 }}
          >
            <Space direction='vertical' size={4}>
              {consentTools.map((tool, index) => {
                const isWrite = tool.kind === 'write'
                const canGoLive = (tool.allowed_modes || []).includes('live')
                const isSelected = selectedTools.includes(tool.tool_key)
                const isLive = liveTools.includes(tool.tool_key)
                // consentTools is ordered reads-then-writes, so the section
                // header goes on the first tool of each kind. "Read my data"
                // and "write to my system" are different decisions and should
                // not read as one undifferentiated list.
                const isFirstOfKind =
                  index === 0 || consentTools[index - 1].kind !== tool.kind
                return (
                  <div key={tool.tool_key}>
                    {isFirstOfKind ? (
                      <Text
                        strong
                        style={{
                          display: 'block',
                          marginTop: index === 0 ? 0 : 10,
                          marginBottom: 2
                        }}
                      >
                        {isWrite
                          ? 'You authorise the agent to WRITE:'
                          : 'You authorise the agent to READ:'}
                      </Text>
                    ) : null}
                    <Checkbox value={tool.tool_key}>
                      <Text>{tool.name || tool.tool_key}</Text>
                      {tool.description ? (
                        <Text type='secondary' style={{ marginLeft: 6 }}>
                          — {tool.description}
                        </Text>
                      ) : null}
                    </Checkbox>
                    {/* Write tools choose HOW they act. "Create live" says
                        plainly what changes; cfg can withhold the live option
                        per tool (allowed_modes), and the backend re-checks. */}
                    {isWrite && isSelected ? (
                      <div style={{ marginLeft: 24, marginTop: 2 }}>
                        <Radio.Group
                          size='small'
                          value={isLive ? 'live' : 'propose'}
                          onChange={(e) => setToolMode(tool.tool_key, e.target.value === 'live')}
                          options={[
                            { value: 'propose', label: 'Ask me first' },
                            { value: 'live', label: 'Create without asking', disabled: !canGoLive }
                          ]}
                          optionType='button'
                        />
                        <Text type='secondary' style={{ marginLeft: 8, fontSize: 12 }}>
                          {isLive
                            ? 'Records appear immediately. You can undo them under Agent activity.'
                            : 'Nothing is created until you approve it under Agent activity.'}
                        </Text>
                      </div>
                    ) : null}
                  </div>
                )
              })}
            </Space>
          </Checkbox.Group>
          {effectiveLiveTools.length > 0 ? (
            <Alert
              type='warning'
              showIcon
              style={{ marginTop: 8 }}
              message='Some capabilities will act without asking'
              description={
                'This agent will create records in Reservoir on your behalf with no approval step. ' +
                'Everything it creates is listed under Agent activity, marked as agent-created, ' +
                'and can be undone there. Revoking this connection stops it within minutes.'
              }
            />
          ) : null}
          {selectedTools.length === 0 ? (
            <Text type='danger'>Select at least one capability.</Text>
          ) : null}
        </div>

        <div>
          <Text strong>Access expires</Text>
          <div style={{ marginTop: 4 }}>
            <Radio.Group
              value={expiryChoice}
              onChange={(e) => setExpiryChoice(e.target.value)}
              options={[
                ...(consent.expiry_days_options || []).map((days) => ({
                  value: days,
                  label: `${days} days`
                })),
                { value: CUSTOM_EXPIRY, label: 'Custom' }
              ]}
            />
            {expiryChoice === CUSTOM_EXPIRY ? (
              <DatePicker
                value={customExpiry}
                onChange={setCustomExpiry}
                style={{ marginTop: 8, width: '100%' }}
                getPopupContainer={(trigger) => (trigger.parentElement as HTMLElement) || document.body}
                disabledDate={(d) =>
                  d.isBefore(dayjs().endOf('day')) ||
                  d.isAfter(dayjs().add(consent.expiry_max_days, 'day'))
                }
              />
            ) : null}
          </div>
          {/* Honest about latency, and it matches Revoke's copy: the gateway
              re-reads the grant per call, so removals bite almost at once,
              but a token already minted keeps its signed write allowlist
              until it expires. */}
          <Text type='secondary'>
            Changes take effect within minutes. Removing a capability stops new use of it
            straight away; a session already running ends within its short lifetime.
          </Text>
        </div>
      </Space>
    </Modal>
  )
}
